import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Target } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { Pill } from '@/components/ui/pill';
import { useMessageQuotas, useSetMessageQuota } from '@/hooks/useAdmin';
import { useTeamScope } from '@/hooks/useTeamScope';
import { useUrlState } from '@/hooks/useUrlState';
import {
  QUOTA_VIEW_LABEL,
  QUOTA_VIEW_TONE,
  QUOTA_VIEWS,
  parseQuotaInput,
  quotaPercent,
  quotaView,
  quotaViewText,
  sortQuotaRows,
  summarizeQuotaWeek,
  weekOf,
  weekRangeLabel,
  type QuotaView,
} from '@/lib/messageQuota';
import { teamLabel } from '@/lib/labels';
import { cn } from '@/lib/utils';
import { Loading, LoadError, Note, Segmented, Th } from '@/pages/admin/insights/parts';
import type { QuotaRow, QuotaWeek } from '@/api/types';

const WEEKS = 8;

type Who = 'retainer' | 'all';
const WHO_OPTIONS: readonly { value: Who; label: string }[] = [
  { value: 'retainer', label: 'Retainer' },
  { value: 'all', label: 'Everyone' },
];

/** Bar fill per state. Under is the only one that reads as a problem. */
const BAR_CLASS: Record<QuotaView, string> = {
  under: 'bg-destructive',
  open: 'bg-warning',
  hit: 'bg-success',
  over: 'bg-brand',
  none: 'bg-muted-foreground/40',
};

/**
 * Message quotas: how many creators each outreacher is expected to message a
 * week, set here, against how many they marked as messaged. One row per
 * active outreacher for the chosen week, furthest behind first, with the
 * last eight weeks alongside.
 *
 * Quotas are mainly for outreachers on a retainer (paid for the work, not
 * per invite), so the page opens on them; "Everyone" adds the rest.
 *
 * A message is a creator marked "I messaged them" (queue or self-found) or
 * with a logged reply, counted in the week they were first messaged. Weeks run Monday to Sunday,
 * UTC, the same as Insights.
 */
export function AdminMessageQuotas(): JSX.Element {
  const scope = useTeamScope();
  const report = useMessageQuotas({ weeks: WEEKS, ...scope.params });
  const [f, setF] = useUrlState({ week: '', show: '', who: 'retainer' });

  const data = report.data;
  const weeks = data?.weeks ?? [];
  const week = weeks.includes(f.week) ? f.week : (data?.currentWeek ?? '');
  const isCurrent = week === data?.currentWeek;
  const at = weeks.indexOf(week);
  const show = (QUOTA_VIEWS as readonly string[]).includes(f.show) ? (f.show as QuotaView) : null;
  const who: Who = f.who === 'all' ? 'all' : 'retainer';
  const inScope = useMemo(
    () => (data?.rows ?? []).filter((r) => who === 'all' || r.compensationModel === 'RETAINER'),
    [data?.rows, who],
  );

  const summary = useMemo(
    () => summarizeQuotaWeek(inScope, week, isCurrent),
    [inScope, week, isCurrent],
  );
  const rows = useMemo(() => {
    const sorted = sortQuotaRows(inScope, week);
    return show ? sorted.filter((r) => quotaView(weekOf(r, week).status, isCurrent) === show) : sorted;
  }, [inScope, week, isCurrent, show]);

  const step = (by: number): void => {
    const next = weeks[at + by];
    if (next) setF({ week: next === data?.currentWeek ? '' : next });
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Message quotas"
        description="Set how many creators each outreacher should message a week, and see who is under, on or over it. A message is a creator they marked as messaged or logged a reply from."
        actions={
          weeks.length > 0 ? (
            <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
              <Segmented label="Which outreachers" value={who} options={WHO_OPTIONS} onChange={(v) => setF({ who: v })} />
              <div className="flex items-center gap-1.5">
              <Button variant="outline" size="icon" aria-label="Previous week" disabled={at <= 0} onClick={() => step(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-[9.5rem] text-center">
                <p className="text-sm font-semibold tabular-nums">{weekRangeLabel(week)}</p>
                <p className="text-xs text-muted-foreground">{isCurrent ? 'This week, so far' : 'Week ended'}</p>
              </div>
              <Button
                variant="outline"
                size="icon"
                aria-label="Next week"
                disabled={at < 0 || at >= weeks.length - 1}
                onClick={() => step(1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              </div>
            </div>
          ) : undefined
        }
      />

      {report.isLoading ? (
        <Loading label="Loading message quotas…" />
      ) : report.error || !data ? (
        <LoadError error={report.error} what="message quotas" />
      ) : data.rows.length === 0 ? (
        <EmptyState
          icon={<Target className="h-6 w-6" />}
          title="No active outreachers here."
          hint="Quotas are set per active outreacher. Add one under Outreachers and they will show up on this page."
        />
      ) : inScope.length === 0 ? (
        <div className="flex flex-col items-center">
          <EmptyState
            icon={<Target className="h-6 w-6" />}
            title="No retainer outreachers here."
            hint="Quotas are mainly for outreachers on a retainer. Show everyone to set one for someone else."
            className="pb-4"
          />
          <Button variant="outline" size="sm" onClick={() => setF({ who: 'all' })}>
            Show everyone
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-stretch gap-2" role="group" aria-label="Filter by quota status">
            {QUOTA_VIEWS.filter((v) => (isCurrent ? v !== 'under' : v !== 'open')).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={show === v}
                onClick={() => setF({ show: show === v ? '' : v })}
                className={cn(
                  'panel min-w-[7.5rem] flex-1 rounded-xl px-4 py-3 text-left transition-colors hover:bg-accent/40',
                  show === v && 'ring-2 ring-brand',
                )}
              >
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <span aria-hidden className={cn('h-2 w-2 rounded-full', BAR_CLASS[v])} />
                  {QUOTA_VIEW_LABEL[v]}
                </span>
                <span className="mt-1 block font-mono text-2xl font-medium tabular-nums tracking-[-0.03em]">
                  {summary.counts[v]}
                </span>
              </button>
            ))}
            <div className="panel min-w-[11rem] flex-[1.4] rounded-xl px-4 py-3">
              <p className="text-xs font-medium text-muted-foreground">Total messaged</p>
              <p className="mt-1 font-mono text-2xl font-medium tabular-nums tracking-[-0.03em]">
                {summary.messages.toLocaleString()}
                {summary.quota > 0 ? (
                  <span className="text-base text-muted-foreground"> / {summary.quota.toLocaleString()}</span>
                ) : null}
              </p>
            </div>
          </div>

          {rows.length === 0 ? (
            <EmptyState title={`Nobody is "${show ? QUOTA_VIEW_LABEL[show] : ''}" this week.`} hint="Clear the filter to see everyone." />
          ) : (
            <div className="table-frame relative overflow-x-auto">
              <table className="responsive-stack w-full text-sm" aria-label="Message quotas">
                <thead>
                  <tr className="text-left">
                    <Th label="Outreacher" numeric={false} />
                    <Th label="Weekly quota" def="Creators to message per week. Leave empty for no quota." />
                    <Th label="Messaged" def="Creators marked as messaged or with a logged reply, counted in the week they were first messaged." />
                    <Th label="Progress" numeric={false} />
                    <Th label="Status" numeric={false} />
                    <Th label={`Last ${weeks.length} weeks`} def="Each bar is a week, oldest first. The line is the quota as it is now." numeric={false} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <QuotaTableRow
                      key={r.inviterId}
                      row={r}
                      week={weekOf(r, week)}
                      selectedWeek={week}
                      currentWeek={data.currentWeek}
                      showTeam={!scope.team}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Note>
            Weeks run Monday to Sunday (UTC). Past weeks are measured against the quota as it is set now. Follow-ups to a
            creator already messaged don't count again. Outreachers with a quota see it on their dashboard as a weekly minimum.
          </Note>
        </>
      )}
    </div>
  );
}

function QuotaTableRow({
  row,
  week,
  selectedWeek,
  currentWeek,
  showTeam,
}: {
  row: QuotaRow;
  week: QuotaWeek;
  selectedWeek: string;
  currentWeek: string;
  showTeam: boolean;
}): JSX.Element {
  const quota = row.weeklyMessageQuota;
  const view = quotaView(week.status, selectedWeek === currentWeek);
  const pct = quotaPercent(week.messages, quota);

  return (
    <tr className="border-t border-border transition-colors hover:bg-accent/40">
      <td className="px-3 py-2">
        <Link to={`/admin/outreachers/${row.publicId}`} className="font-medium hover:underline">
          {row.name}
        </Link>
        {showTeam ? <span className="ml-2 text-xs text-muted-foreground">{teamLabel(row.team)}</span> : null}
      </td>
      <td className="px-3 py-2 text-right" data-label="Weekly quota">
        <QuotaInput inviterId={row.inviterId} name={row.name} quota={quota} />
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums" data-label="Messaged">
        {week.messages.toLocaleString()}
      </td>
      <td className="px-3 py-2" data-label="Progress">
        {quota === null ? (
          <span className="text-xs text-muted-foreground">Set a quota to track</span>
        ) : (
          <div className="flex min-w-[10rem] items-center gap-2">
            <div
              className="h-2 flex-1 overflow-hidden rounded-full bg-muted"
              role="img"
              aria-label={pct === null ? 'No target' : `${pct}% of quota`}
            >
              <div className={cn('h-full rounded-full', BAR_CLASS[view])} style={{ width: `${Math.min(pct ?? 100, 100)}%` }} />
            </div>
            <span className="w-11 text-right font-mono text-xs tabular-nums text-muted-foreground">
              {pct === null ? '–' : `${pct}%`}
            </span>
          </div>
        )}
      </td>
      <td className="px-3 py-2" data-label="Status">
        <Pill tone={QUOTA_VIEW_TONE[view]}>{quotaViewText(view, week.messages, quota)}</Pill>
      </td>
      <td className="px-3 py-2" data-label="History">
        <WeekSpark row={row} selectedWeek={selectedWeek} currentWeek={currentWeek} />
      </td>
    </tr>
  );
}

/** One bar per week, scaled to the row's busiest week or its quota. */
function WeekSpark({
  row,
  selectedWeek,
  currentWeek,
}: {
  row: QuotaRow;
  selectedWeek: string;
  currentWeek: string;
}): JSX.Element {
  const quota = row.weeklyMessageQuota;
  const top = Math.max(quota ?? 0, ...row.weeks.map((w) => w.messages), 1);
  return (
    <div className="relative flex h-8 w-[8.5rem] items-end gap-[3px]">
      {row.weeks.map((w) => {
        const view = quotaView(w.status, w.weekStart === currentWeek);
        return (
          <span
            key={w.weekStart}
            title={`${weekRangeLabel(w.weekStart)}: ${w.messages.toLocaleString()}${quota === null ? '' : ` of ${quota.toLocaleString()}`}`}
            className={cn(
              'flex-1 rounded-sm',
              BAR_CLASS[view],
              w.weekStart === selectedWeek ? 'opacity-100' : 'opacity-45',
            )}
            style={{ height: `${Math.max((w.messages / top) * 100, w.messages > 0 ? 8 : 3)}%` }}
          />
        );
      })}
      {quota !== null && quota > 0 ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 border-t border-dashed border-foreground/50"
          style={{ bottom: `${(quota / top) * 100}%` }}
        />
      ) : null}
    </div>
  );
}

/** Inline quota box: saves on blur or Enter, Escape puts the old value back. */
function QuotaInput({ inviterId, name, quota }: { inviterId: number; name: string; quota: number | null }): JSX.Element {
  const save = useSetMessageQuota();
  const saved = quota === null ? '' : String(quota);
  const [value, setValue] = useState(saved);
  useEffect(() => setValue(saved), [saved]);

  const commit = (): void => {
    const next = parseQuotaInput(value);
    if (next === undefined) {
      toast.error('Quota must be a whole number, or empty for none.');
      setValue(saved);
      return;
    }
    if (next === quota) {
      setValue(saved);
      return;
    }
    save.mutate(
      { id: inviterId, quota: next },
      {
        onSuccess: () =>
          toast.success(next === null ? `Cleared ${name}'s quota` : `${name}: ${next.toLocaleString()} a week`),
        onError: (err) => {
          toast.error((err as Error).message);
          setValue(saved);
        },
      },
    );
  };

  return (
    <Input
      inputMode="numeric"
      aria-label={`Weekly message quota for ${name}`}
      placeholder="None"
      value={value}
      disabled={save.isPending}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setValue(saved);
          // Blur after the state settles so commit sees the old value.
          const el = e.currentTarget;
          requestAnimationFrame(() => el.blur());
        }
      }}
      className="ml-auto h-8 w-24 text-right font-mono tabular-nums"
    />
  );
}
